import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export function ArchitectureVisualizer() {
  const [activeTab, setActiveTab] = useState<"cloud" | "hardware">("cloud");
  const [selectedNode, setSelectedNode] = useState<number | null>(null);

  const cloudNodes = [
    {
      id: 1,
      name: "Security & Edge Ingress",
      protocol: "HTTPS / TLS 1.3",
      latency: "Edge Gateway",
      desc: "API rate limiting, cryptographic token validation, and DPDP-aligned data minimization.",
      tech: ["Bun Native HTTP", "Zero-Trust RBAC", "WAF Rules"],
    },
    {
      id: 2,
      name: "Process Runtime & Task Engine",
      protocol: "Bun TypeScript / gRPC",
      latency: "Sub-Millisecond",
      desc: "Stateless route dispatch, visual DAG task orchestration, and modular service isolation.",
      tech: ["Bun Engine", "DAG Scheduler", "Audit Logging"],
    },
    {
      id: 3,
      name: "Real-Time WebSocket Sync",
      protocol: "Native WebSockets",
      latency: "Live Bi-Directional",
      desc: "Low-latency state replication across connected client dashboards and active sessions.",
      tech: ["Bun Native WS", "Heartbeat Sync", "Event Bus"],
    },
    {
      id: 4,
      name: "Multi-Tenant Data Store",
      protocol: "PostgreSQL / SQLite",
      latency: "Encrypted at Rest",
      desc: "Row-level tenant isolation, AES-256 field-level encryption, and immutable audit trails.",
      tech: ["RLS Isolation", "AES-256-GCM", "Encrypted Backups"],
    },
  ];

  const hardwareNodes = [
    {
      id: 1,
      name: "Edge Microcontroller Node",
      protocol: "I2C / SPI / 4-20mA",
      latency: "Deep-Sleep Ready",
      desc: "Deterministic ESP32-S3 and ARM Cortex firmware reading industrial sensor telemetry.",
      tech: ["Embedded C/C++", "Low Quiescent LDO", "FreeRTOS Tasks"],
    },
    {
      id: 2,
      name: "LoRaWAN 865MHz RF Mesh",
      protocol: "Semtech SX1262 LoRa",
      latency: "10km+ Range",
      desc: "Long-range wireless transmission designed to overcome metallic structural attenuation.",
      tech: ["India ISM Band", "Adaptive Data Rate", "AES-128 RF Crypto"],
    },
    {
      id: 3,
      name: "Multi-Channel Edge Gateway",
      protocol: "TLS Backhaul / 4G",
      latency: "Packet Ingestion",
      desc: "8-channel concurrent radio receiver with in-flight deduplication and CRC frame checks.",
      tech: ["Packet Forwarder", "Failover Backhaul", "Edge Filtering"],
    },
    {
      id: 4,
      name: "Cloud Telemetry & Ingestion",
      protocol: "MQTT / Time-Series",
      latency: "Real-Time Stream",
      desc: "Centralized metric streaming, threshold anomaly triggers, and automated alert pipelines.",
      tech: ["MQTT Broker", "Time-Series Store", "Alert Webhooks"],
    },
  ];

  const currentNodes = activeTab === "cloud" ? cloudNodes : hardwareNodes;

  return (
    <div className="w-full max-w-5xl mx-auto mt-12 bg-[#080d16] border border-slate-800/90 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden text-left">
      {/* Ambient background glow */}
      <div
        className="absolute -right-20 -top-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute -left-20 -bottom-20 w-72 h-72 bg-sky-500/10 rounded-full blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      {/* Mode Switcher Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800/80 relative z-10">
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
            ● Reference Engineering Blueprint
          </span>
          <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            {activeTab === "cloud"
              ? "Cloud Platforms & Microservices Blueprint (RaBuOS Core)"
              : "Embedded IoT & Wireless Hardware Telemetry Blueprint"}
          </h3>
        </div>

        {/* Tab Buttons */}
        <div className="inline-flex p-1 bg-slate-900/90 border border-slate-800 rounded-xl shrink-0">
          <button
            onClick={() => {
              setActiveTab("cloud");
              setSelectedNode(null);
            }}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
              activeTab === "cloud"
                ? "bg-primary text-slate-950 shadow-md"
                : "text-slate-400 hover:text-white"
            }`}
          >
            ☁️ Cloud & Platform Blueprint
          </button>
          <button
            onClick={() => {
              setActiveTab("hardware");
              setSelectedNode(null);
            }}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
              activeTab === "hardware"
                ? "bg-primary text-slate-950 shadow-md"
                : "text-slate-400 hover:text-white"
            }`}
          >
            ⚡ Hardware & IoT Blueprint
          </button>
        </div>
      </div>

      {/* Interactive System Pipeline Flow */}
      <div className="py-8 relative z-10">
        <p className="text-xs text-slate-400 font-mono mb-4 uppercase tracking-wider flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Reference System Architecture Flow (Click any stage to inspect engineering specifications)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative">
          {currentNodes.map((node, idx) => {
            const isSelected = selectedNode === node.id;
            return (
              <motion.div
                key={node.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.08 }}
                onClick={() => setSelectedNode(isSelected ? null : node.id)}
                className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer flex flex-col justify-between relative group ${
                  isSelected
                    ? "bg-[#101c2a] border-primary shadow-lg shadow-primary/20 ring-1 ring-primary"
                    : "bg-[#0b131e]/90 border-slate-800 hover:border-slate-700 hover:bg-[#0f1a28]"
                }`}
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-[10px] font-mono font-bold text-primary px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                      STAGE 0{node.id}
                    </span>
                    <span className="text-[11px] font-mono text-emerald-400">
                      {node.latency}
                    </span>
                  </div>

                  <h4 className="text-sm font-bold text-white mb-1.5 group-hover:text-primary transition-colors">
                    {node.name}
                  </h4>

                  <p className="text-[11px] font-mono text-slate-400 mb-3">
                    {node.protocol}
                  </p>

                  <p className="text-xs text-slate-300 font-light leading-relaxed mb-4">
                    {node.desc}
                  </p>
                </div>

                <div className="flex flex-wrap gap-1.5 pt-3 border-t border-slate-800/80">
                  {node.tech.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] bg-slate-900 text-slate-300 px-2 py-0.5 rounded border border-slate-800"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {/* Animated Pipeline Arrow on Desktop */}
                {idx < currentNodes.length - 1 && (
                  <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-20 text-slate-600 font-bold text-lg pointer-events-none">
                    →
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Selected Node Details Drawer */}
      <AnimatePresence>
        {selectedNode !== null && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 p-5 bg-[#101c2a] border border-primary/40 rounded-xl overflow-hidden text-slate-200"
          >
            {(() => {
              const node = currentNodes.find((n) => n.id === selectedNode);
              if (!node) return null;
              return (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <span className="text-xs font-bold text-primary uppercase tracking-wider">
                      Blueprint Specification: {node.name}
                    </span>
                    <p className="text-sm text-slate-300 mt-1">
                      {node.desc} Engineered using {node.tech.join(", ")} targeting deterministic execution.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <a
                      href="/contact"
                      className="px-4 py-2 bg-primary text-slate-950 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-sky-400 transition-colors"
                    >
                      Inquire on Custom Architecture →
                    </a>
                    <button
                      onClick={() => setSelectedNode(null)}
                      className="px-3 py-2 bg-slate-800 text-slate-400 hover:text-white rounded-lg text-xs font-bold cursor-pointer"
                    >
                      Close ✕
                    </button>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
